import {
  FuelEconomyReportApiFactory,
  IdlingReportsApiFactory,
  ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest,
  ApiAppFueleconomyreportGetdriverfuelconsumptionPostRequest,
  FuelConsumptionCardsDto,
  FuelConsumptionByActionDto,
  FuelConsumptionByPeriodDto,
  FuelConsumptionByTimeOfDayDto,
  FuelConsumptionEfficiencyDto,
  DriversFuelConsumptionDtoPagedResultDto,
  WorstPerformersFuelConsumptionDto,
  TotalFuelConsumptionDto,
  PeriodType,
} from "../../../common/data/service-proxies";
import { useApiRequest } from "./useApiRequest";
import type { BaseHookReturn } from "./useApiRequest";

export interface FuelEconomyHookParams {
  fromDate: Date;
  toDate: Date;
  periodType?: PeriodType;
}

export interface FuelEconomyDriversHookParams extends FuelEconomyHookParams {
  skipCount?: number;
  maxResultCount?: number;
}

export type { BaseHookReturn } from "./useApiRequest";

// Hook for average fuel consumption
export const useAverageFuelConsumption = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionCardsDto> => {
  return useApiRequest<FuelConsumptionCardsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetaveragefuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useAverageFuelConsumption" },
  );
};

// Hook for idle fuel consumption (gallons)

export const useIdleFuelConsumAvg = (
  params: FuelEconomyHookParams,
): BaseHookReturn<TotalFuelConsumptionDto | null> => {
  return useApiRequest<TotalFuelConsumptionDto | null>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = {
        startTime: params.fromDate.toISOString(),
        endTime: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGettotalidlingfuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useIdleFuelConsumAvg" },
  );
};

// Hook for speeding consumption
export const useSpeedingConsumption = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionCardsDto> => {
  return useApiRequest<FuelConsumptionCardsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetspeedingfuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useSpeedingConsumption" },
  );
};

// Hook for max fuel consumption
export const useMaxFuelConsumption = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionCardsDto> => {
  return useApiRequest<FuelConsumptionCardsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetmaxfuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useMaxFuelConsumption" },
  );
};

// Hook for min fuel consumption
export const useMinFuelConsumption = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionCardsDto> => {
  return useApiRequest<FuelConsumptionCardsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetminfuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useMinFuelConsumption" },
  );
};

// Hook for accelerate consumption
export const useAccelerateConsumption = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionCardsDto> => {
  return useApiRequest<FuelConsumptionCardsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetacceleratefuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useAccelerateConsumption" },
  );
};

/** Aggregated return type for base metrics (average, idle, speeding, max, min, accelerate). */
export interface FuelEconomyBaseMetricsReturn {
  average: BaseHookReturn<FuelConsumptionCardsDto>;
  idle: BaseHookReturn<TotalFuelConsumptionDto | null>;
  speeding: BaseHookReturn<FuelConsumptionCardsDto>;
  max: BaseHookReturn<FuelConsumptionCardsDto>;
  min: BaseHookReturn<FuelConsumptionCardsDto>;
  accelerate: BaseHookReturn<FuelConsumptionCardsDto>;
}

/** Single hook that fetches all base metrics (calls the six individual hooks internally). */
export const useFuelEconomyBaseMetrics = (
  params: FuelEconomyHookParams,
): FuelEconomyBaseMetricsReturn => {
  const average = useAverageFuelConsumption(params);
  const idle = useIdleFuelConsumAvg(params);
  const speeding = useSpeedingConsumption(params);
  const max = useMaxFuelConsumption(params);
  const min = useMinFuelConsumption(params);
  const accelerate = useAccelerateConsumption(params);
  return { average, idle, speeding, max, min, accelerate };
};

// Hook for fuel economy in MPG (by period)
export const useFuelEconomyInMpg = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionByPeriodDto> => {
  return useApiRequest<FuelConsumptionByPeriodDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetfuelconsumptionbyperiodPost(request);
      return response.data;
    },
    { logLabel: "useFuelEconomyInMpg" },
  );
};

// Hook for worst performers fuel consumption
export const useFuelWorstPerformance = (
  params: FuelEconomyHookParams,
): BaseHookReturn<WorstPerformersFuelConsumptionDto[]> => {
  return useApiRequest<WorstPerformersFuelConsumptionDto[]>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetworstperformersfuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useFuelWorstPerformance" },
  );
};

// Hook for fuel economy by action
export const useFuelEconomyByAction = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionByActionDto> => {
  return useApiRequest<FuelConsumptionByActionDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetfuelconsumptionbyactionPost(request);
      return response.data;
    },
    { logLabel: "useFuelEconomyByAction" },
  );
};

// Hook for fuel efficiency
export const useFuelEfficiency = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionEfficiencyDto> => {
  return useApiRequest<FuelConsumptionEfficiencyDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetfuelconsumptionefficiencyPost(request);
      return response.data;
    },
    { logLabel: "useFuelEfficiency" },
  );
};

// Hook for fuel economy by time of day
export const useFuelEconomyByTimeOfDay = (
  params: FuelEconomyHookParams,
): BaseHookReturn<FuelConsumptionByTimeOfDayDto> => {
  return useApiRequest<FuelConsumptionByTimeOfDayDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetaveragefuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetfuelconsumptionbytimeofdayPost(request);
      return response.data;
    },
    { logLabel: "useFuelEconomyByTimeOfDay" },
  );
};

// Hook for fuel drivers
export const useFuelDrivers = (
  params: FuelEconomyDriversHookParams,
): BaseHookReturn<DriversFuelConsumptionDtoPagedResultDto> => {
  return useApiRequest<DriversFuelConsumptionDtoPagedResultDto>(
    [
      params.fromDate,
      params.toDate,
      params.periodType,
      params.skipCount,
      params.maxResultCount,
    ],
    async (axios, baseURL) => {
      const request: ApiAppFueleconomyreportGetdriverfuelconsumptionPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
        skipCount: params.skipCount,
        maxResultCount: params.maxResultCount,
      };

      const response = await FuelEconomyReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppFueleconomyreportGetdriverfuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useFuelDrivers" },
  );
};
